// src/pages/TermsAndService.js
import React from "react";
import "./TermsAndPolicy.css";

const TermsAndService = () => {
  return (
    <div className="terms-and-policy-container">
      <h1>Andeon Apps Terms of Service</h1>
      <p>Last Updated: May 25, 2026</p>

      <h2>1. Acceptance of Terms</h2>
      <p>
        These Terms of Service ("Terms") govern your access to and use of
        Andeon Apps (the "Service"), an AI-powered image-to-video tool
        operated by Andeon Apps ("we," "us," or "our"). By creating an
        account or otherwise using the Service, you agree to be bound by
        these Terms, our Privacy Policy, and our Refund Policy, each of
        which is incorporated by reference. If you do not agree, you may
        not use the Service.
      </p>
      <p>
        <strong>Please read Section 19 carefully.</strong> It requires
        disputes to be resolved by binding individual arbitration and
        waives your right to participate in a class action.
      </p>

      <h2>2. Eligibility</h2>
      <p>
        The Service is offered only to individuals who are at least
        eighteen (18) years old and located in the United States. By
        using the Service, you represent and warrant that you meet these
        requirements and that you have the legal authority to enter into
        these Terms.
      </p>

      <h2>3. Changes to Terms</h2>
      <p>
        We may modify these Terms at any time. If we make material
        changes, we will provide reasonable notice — for example, by
        posting the updated Terms on this page and updating the "Last
        Updated" date, or by notifying you within the Service. Your
        continued use of the Service after changes become effective
        constitutes acceptance of the revised Terms.
      </p>

      <h2>4. Accounts</h2>
      <p>
        To use the Service you must create an account. You agree to
        provide accurate information, keep your credentials confidential,
        and remain responsible for all activity that occurs under your
        account. Notify us promptly of any unauthorized use or suspected
        security incident.
      </p>

      <h2>5. Subscriptions, Credits, and Billing</h2>
      <p>
        Paid plans and credit packs are billed through our payment
        processor, Stripe. By purchasing, you authorize us (through
        Stripe) to charge your selected payment method for all fees,
        taxes, and applicable charges.
      </p>
      <ul>
        <li>
          <strong>Pricing, currency, and taxes.</strong> Prices are
          displayed in U.S. dollars and exclude applicable sales tax,
          VAT, or similar charges, which will be added at checkout where
          required. Charges will appear on your statement under the
          descriptor "ANDEON APPS" or "FRICORI."
        </li>
        <li>
          <strong>Subscriptions</strong> renew automatically at the end
          of each billing period at the then-current rate, unless you
          cancel before the renewal date.
        </li>
        <li>
          <strong>Credits</strong> are non-transferable, have no cash
          value, and reset at the end of each subscription billing
          period; unused credits do not roll over to the next period.
          Upon cancellation or termination of your subscription, any
          remaining credit balance is forfeited.
        </li>
        <li>
          <strong>Cancellation.</strong> You can cancel your subscription
          at any time from your account settings. Cancellation stops
          future renewals; access continues until the end of the current
          billing period. See our Refund Policy for details on refunds.
        </li>
        <li>
          <strong>Price changes.</strong> We may change subscription
          pricing on a prospective basis; we will notify you of any
          change before it takes effect, and continued use after the
          change constitutes acceptance.
        </li>
        <li>
          <strong>Billing disputes.</strong> Please contact us at
          legal@andeonapps.com before initiating a chargeback so we can
          resolve the issue directly. Failed payments, chargebacks, or
          fraudulent purchases may result in suspension or termination
          of your account.
        </li>
      </ul>

      <h2>6. User Content</h2>
      <p>
        "User Content" means the images, prompts, and other materials
        you upload to or submit through the Service. You retain
        ownership of your User Content. You represent and warrant that
        you own or have the necessary rights, licenses, and permissions
        to upload your User Content and to authorize the processing
        described in these Terms, and that your User Content does not
        violate the rights of any third party or any applicable law.
      </p>

      <h2>7. License to Process Your Content</h2>
      <p>
        Solely for the purpose of operating and improving the Service,
        you grant Andeon Apps a worldwide, non-exclusive, royalty-free
        license to host, store, transmit, modify, display, and process
        your User Content, including sending it to third-party AI
        providers (currently xAI) so they can generate videos for you.
        This license ends when you delete your User Content or your
        account, on the timeline described in Section 6 of our Privacy
        Policy (thirty days from active systems, ninety days from
        backups), except as required by law.
      </p>

      <h2>8. Generated Content and Commercial Use</h2>
      <p>
        Subject to your compliance with these Terms and any applicable
        third-party model terms, you may use the videos generated for
        you through the Service ("Generated Content") for personal or
        commercial purposes. Because Generated Content is produced by AI
        models we do not control, we make no representations regarding
        its originality, accuracy, or non-infringement. Similar or
        identical outputs may be generated for other users.
      </p>
      <p>
        <strong>Commercial use is at your own risk.</strong> If you
        intend to use Generated Content commercially — including in
        product marketing, advertising, or resale of goods — you are
        solely responsible for independently clearing all applicable
        rights before publication, including trademark, copyright,
        right-of-publicity, and likeness rights. You must not upload
        third-party product photographs without authorization from the
        rights-holder.
      </p>

      <h2>9. Acceptable Use</h2>
      <p>You agree not to use the Service to upload, generate, or distribute:</p>
      <ul>
        <li>
          Content that is unlawful, harassing, defamatory, hateful, or
          that infringes the intellectual property, privacy, or publicity
          rights of others;
        </li>
        <li>
          Child sexual abuse material (CSAM), including any synthetic,
          generated, or apparent depiction of a minor in a sexual
          context, and any other content that exploits or endangers
          minors;
        </li>
        <li>
          Non-consensual intimate imagery of any person, including
          synthetic nudity or sexualization of an identifiable real
          adult without their documented consent;
        </li>
        <li>
          Deceptive depictions of real, identifiable people (including
          political figures, celebrities, or private individuals)
          without their consent, or content intended to deceive,
          defraud, impersonate, or interfere with an election or
          democratic process;
        </li>
        <li>
          Content intended to facilitate violence, terrorism, self-harm,
          the manufacture of weapons, malware, phishing, or other
          illegal acts;
        </li>
        <li>
          Claims or representations in regulated fields (such as medical,
          legal, financial, or insurance advice) made in a manner that
          could reasonably be relied upon as professional advice;
        </li>
        <li>
          Any content that violates the acceptable-use policies of our
          underlying AI providers (including xAI).
        </li>
      </ul>
      <p>
        You also agree not to: (a) attempt to reverse-engineer,
        decompile, or extract the underlying models or source code of
        the Service; (b) use the Service, its inputs, or its outputs to
        develop, train, fine-tune, or benchmark a service that competes
        with the Service; (c) interfere with, overburden, or disrupt the
        Service; or (d) bypass usage limits, credit checks, or access
        controls, or use automated means to access the Service except as
        expressly permitted.
      </p>
      <p>
        We may remove content and suspend or terminate accounts that
        violate these rules. Where required by law, we will report
        suspected child sexual abuse material to the National Center for
        Missing &amp; Exploited Children's CyberTipline.
      </p>

      <h2>10. Copyright Complaints (DMCA)</h2>
      <p>
        If you believe content on the Service infringes your copyright,
        please send a written notice to our copyright contact that
        includes the elements set out in the Digital Millennium
        Copyright Act (17 U.S.C. § 512(c)(3)):
      </p>
      <ul>
        <li>
          A physical or electronic signature of the copyright owner or
          authorized agent;
        </li>
        <li>Identification of the copyrighted work claimed to be infringed;</li>
        <li>
          Identification of the material claimed to be infringing,
          including its location on the Service;
        </li>
        <li>Your contact information (address, telephone, and email);</li>
        <li>
          A statement that you have a good-faith belief that the use is
          not authorized by the copyright owner, its agent, or the law;
        </li>
        <li>
          A statement, under penalty of perjury, that the information in
          the notice is accurate and that you are the copyright owner or
          authorized to act on the owner's behalf.
        </li>
      </ul>
      <p>
        Send notices to dmca@andeonapps.com. If we remove your content
        in response to such a notice, you may submit a counter-notice
        containing the elements required by 17 U.S.C. § 512(g). We will
        terminate the accounts of users who are determined to be repeat
        infringers.
      </p>

      <h2>11. Intellectual Property</h2>
      <p>
        Except for your User Content and the Generated Content licensed
        to you above, Andeon Apps and its licensors own all right,
        title, and interest in and to the Service, including the
        software, design, trademarks, and underlying technology. These
        Terms do not grant you any right to use Andeon Apps or Fricori
        trademarks without our prior written consent.
      </p>

      <h2>12. Third-Party Services</h2>
      <p>
        The Service relies on third-party services (including, without
        limitation, xAI, Supabase, Stripe, and our hosting providers).
        Outages, errors, or policy changes by those providers may affect
        the Service. We are not responsible for the acts or omissions of
        third-party providers, and your use of any third-party service
        is subject to that provider's own terms and policies.
      </p>

      <h2>13. Service Availability</h2>
      <p>
        We work to keep the Service available, but we do not guarantee
        it will be uninterrupted or error-free. We may modify, suspend,
        or discontinue all or part of the Service at any time, with or
        without notice. We will not be liable for any modification,
        suspension, or discontinuation of the Service.
      </p>

      <h2>14. Termination</h2>
      <p>
        You may stop using the Service and delete your account at any
        time. We may suspend or terminate your access immediately,
        without prior notice or liability, if you breach these Terms, if
        required by law, or to protect the Service or its users.
      </p>
      <p>
        If we terminate your account for cause (including violation of
        Section 9), any unused credit balance and any prepaid
        subscription period are forfeited and no refund is owed. If we
        terminate your account for our own convenience without cause,
        we will refund the pro-rata unused portion of your current
        subscription period. Upon termination, your right to use the
        Service will cease and we will delete your account data on the
        timeline described in Section 6 of our Privacy Policy.
      </p>

      <h2>15. Disclaimer of Warranties</h2>
      <p>
        THE SERVICE AND ALL GENERATED CONTENT ARE PROVIDED "AS IS" AND
        "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS
        OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY,
        FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, AND ANY
        WARRANTIES ARISING OUT OF COURSE OF DEALING OR USAGE OF TRADE.
        WE DO NOT WARRANT THAT THE SERVICE WILL MEET YOUR REQUIREMENTS,
        BE UNINTERRUPTED, SECURE, OR ERROR-FREE, OR THAT GENERATED
        CONTENT WILL BE ACCURATE, ORIGINAL, OR SUITABLE FOR YOUR
        INTENDED USE.
      </p>

      <h2>16. Limitation of Liability</h2>
      <p>
        TO THE FULLEST EXTENT PERMITTED BY LAW, IN NO EVENT SHALL ANDEON
        APPS, ITS DIRECTORS, EMPLOYEES, AFFILIATES, OR AGENTS BE LIABLE
        FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR
        PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, GOODWILL, OR
        OTHER INTANGIBLE LOSSES, ARISING OUT OF OR IN CONNECTION WITH
        YOUR USE OF OR INABILITY TO USE THE SERVICE. OUR TOTAL LIABILITY
        FOR ANY CLAIM ARISING OUT OF OR RELATING TO THE SERVICE OR
        THESE TERMS SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNTS YOU
        PAID TO US FOR THE SERVICE IN THE TWELVE (12) MONTHS PRECEDING
        THE EVENT GIVING RISE TO THE CLAIM, OR (B) ONE HUNDRED U.S.
        DOLLARS (USD $100).
      </p>
      <p>
        Nothing in these Terms limits or excludes liability that cannot
        lawfully be limited or excluded, including liability for fraud,
        gross negligence, willful misconduct, or death or personal
        injury caused by negligence.
      </p>

      <h2>17. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless Andeon Apps
        and its officers, directors, employees, and agents from and
        against any claims, liabilities, damages, losses, and expenses
        (including reasonable attorneys' fees) arising out of or in any
        way connected with: (i) your User Content; (ii) your use of the
        Service or Generated Content; (iii) your violation of these
        Terms; or (iv) your violation of any rights of another.
      </p>
      <p>
        Our obligations under this Section are conditioned on us: (a)
        promptly notifying you in writing of the claim; (b) granting you
        sole control over the defense and settlement of the claim,
        provided that no settlement may impose any obligation or
        admission on us without our prior written consent; and (c)
        providing you with reasonable cooperation, at your expense.
      </p>

      <h2>18. Governing Law</h2>
      <p>
        These Terms shall be governed by and construed in accordance
        with the laws of the State of New York, without regard to its
        conflict of law provisions.
      </p>

      <h2>19. Dispute Resolution; Arbitration; Class-Action Waiver</h2>
      <p>
        <strong>Binding arbitration.</strong> Any dispute, claim, or
        controversy arising out of or relating to these Terms or the
        Service ("Dispute") shall be finally settled by binding
        individual arbitration administered by JAMS in New York, New
        York, in accordance with the JAMS Streamlined Arbitration Rules
        and Procedures then in effect, by one commercial arbitrator
        with substantial experience in technology and commercial
        contract disputes. Judgment on the award may be entered in any
        court having jurisdiction.
      </p>
      <p>
        <strong>Delegation.</strong> The arbitrator, and not any
        federal, state, or local court or agency, shall have exclusive
        authority to resolve any dispute relating to the
        interpretation, applicability, enforceability, or formation of
        this arbitration agreement, including any claim that all or
        any part of it is void or voidable.
      </p>
      <p>
        <strong>CLASS-ACTION WAIVER.</strong> YOU AND ANDEON APPS AGREE
        THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR
        ITS INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER
        IN ANY PURPORTED CLASS, COLLECTIVE, CONSOLIDATED, PRIVATE
        ATTORNEY GENERAL, OR REPRESENTATIVE PROCEEDING. THE ARBITRATOR
        MAY NOT CONSOLIDATE MORE THAN ONE PERSON'S CLAIMS, AND MAY NOT
        OTHERWISE PRESIDE OVER ANY FORM OF A REPRESENTATIVE OR CLASS
        PROCEEDING.
      </p>
      <p>
        <strong>Exceptions.</strong> Notwithstanding the foregoing,
        either party may bring an individual action in small-claims
        court for qualifying disputes, and either party may seek
        injunctive or other equitable relief in a court of competent
        jurisdiction to protect its intellectual property rights.
      </p>
      <p>
        <strong>Time limit.</strong> Any Dispute must be filed within
        one (1) year after the cause of action arose; otherwise the
        Dispute is permanently barred.
      </p>

      <h2>20. Miscellaneous</h2>
      <p>
        <strong>Entire agreement.</strong> These Terms, together with
        the Privacy Policy and Refund Policy, constitute the entire
        agreement between you and Andeon Apps regarding the Service and
        supersede any prior agreements.
      </p>
      <p>
        <strong>Severability.</strong> If any provision is found to be
        unenforceable, the remaining provisions will remain in full
        force and effect.
      </p>
      <p>
        <strong>No waiver.</strong> Our failure to enforce any right or
        provision is not a waiver of that right or provision.
      </p>
      <p>
        <strong>Assignment.</strong> You may not assign or transfer
        these Terms without our prior written consent; we may assign
        them without restriction.
      </p>
      <p>
        <strong>Force majeure.</strong> Neither party will be liable
        for any failure or delay caused by events beyond its reasonable
        control, including acts of God, war, terrorism, civil unrest,
        labor disputes, internet or utility outages, or actions of
        governmental authorities.
      </p>
      <p>
        <strong>Electronic communications and consent.</strong> By
        using the Service, you consent to receive communications from
        us electronically, and you agree that all agreements, notices,
        disclosures, and other communications we provide to you
        electronically satisfy any legal requirement that such
        communications be in writing.
      </p>
      <p>
        <strong>Export controls and sanctions.</strong> You represent
        that you are not located in, under the control of, or a
        national or resident of any country subject to U.S. embargo, and
        that you are not on any U.S. government list of restricted or
        prohibited parties.
      </p>
      <p>
        <strong>Notices.</strong> We may give notice to you by email to
        the address associated with your account or by posting on the
        Service. You may give notice to us at legal@andeonapps.com.
      </p>
      <p>
        <strong>No third-party beneficiaries.</strong> These Terms do
        not create any third-party beneficiary rights.
      </p>
      <p>
        <strong>Survival.</strong> Sections 5 (for amounts owed), 6, 7
        (for the limited backup-retention period), 8, 11, and 15
        through 20 survive any termination of these Terms.
      </p>

      <h2>21. Contact Us</h2>
      <p>
        If you have any questions about these Terms, please contact us
        at legal@andeonapps.com.
      </p>
    </div>
  );
};

export default TermsAndService;
